/**
 * The captions layer, placed: from the transcripts kept per source (spec.transcript), the lines each scene really
 * plays — a creator's clip shows the lines inside its window of the file (at its speed), a voice-over clip the lines
 * inside its slice. Only what is HEARD is captioned: a muted creator gets none (you would read words you cannot
 * hear). Shared by the renderer (CaptionLayer), the timeline's Captions track and the editor.
 */
import { VIDEO_FPS, sceneStarts, voicePartsOf, type TranscriptWord, type VideoScene, type VideoSpec } from './spec';

export interface CaptionCue {
  /** Stable within one version of the plan (scene or voice clip + line). */
  key: string;
  /** The source the line comes from, and its index there (edits go back to it). */
  url: string;
  line: number;
  start: number;
  end: number;
  text: string;
  /** Word timings in video seconds (when the source was transcribed to the word and is in the video's language). */
  words?: TranscriptWord[];
  /** The scene it plays over, and where captions sit in it (the layout pass keeps them off faces and the product). */
  scene: number;
  band: 'top' | 'middle' | 'bottom';
  from: 'creator' | 'voice-over';
  /** The creator burned in their own subtitles here: ours stay hidden unless the person shows them. */
  hidden: boolean;
  /** Video time ↔ source time for this window (source = a + (t − at) × rate), and the window's span in video time —
   *  a line edited on the timeline is written back to its source and stays inside the clip it plays in. */
  map: { at: number; a: number; rate: number };
  bounds: [number, number];
}

const bandOf = (s: VideoScene | undefined): CaptionCue['band'] => {
  if (!s) return 'bottom';
  const place = (s as { place?: { captions?: CaptionCue['band']; overlay?: CaptionCue['band'] } }).place;
  if (s.kind === 'hook') return place?.overlay === 'bottom' ? 'top' : 'bottom';
  return place?.captions || 'bottom';
};

export function captionCues(spec: VideoSpec): CaptionCue[] {
  const tr = spec.transcript;
  if (!tr) return [];
  const starts = sceneStarts(spec).map((f) => f / VIDEO_FPS);
  const out: CaptionCue[] = [];
  // While a hook line is on screen it is the one thing to read: the creator's caption under it would be a second text
  // block fighting it (the frame review's "crowding"). Those cues stay on the timeline, hidden.
  const underHook = (i: number) => { const sc = spec.scenes[i] as { kind: string; text?: string }; return sc?.kind === 'hook' && !!sc.text?.trim(); };
  const place = (url: string, a: number, b: number, rate: number, at: number, scene: number, from: CaptionCue['from'], tag: string) => {
    const src = tr.sources[url]; if (!src) return;
    const toV = (t: number) => Math.round((at + (Math.max(a, Math.min(b, t)) - a) / rate) * 1000) / 1000;
    src.lines.forEach((l, k) => {
      const overlap = Math.min(b, l.e) - Math.max(a, l.s);
      // At least half the line inside the window (a line mostly said before the cut isn't heard here); a line the cut
      // clips shows only the words said inside it.
      if (overlap <= 0 || overlap < (l.e - l.s) * 0.5) return;
      const words = l.words?.filter((w) => (w.s + w.e) / 2 > a && (w.s + w.e) / 2 < b).map((w) => ({ w: w.w, s: toV(w.s), e: toV(w.e) }));
      const text = words?.length && words.length < (l.words?.length || 0) ? words.map((w) => w.w).join(' ') : l.text;
      out.push({ key: `${tag}-${k}`, url, line: k, start: toV(l.s), end: toV(l.e), text, ...(words?.length ? { words } : {}), scene, band: bandOf(spec.scenes[scene]), from, hidden: !!src.burned || (from === 'creator' && underHook(scene)), map: { at, a, rate }, bounds: [at, toV(b)] });
    });
  };
  spec.scenes.forEach((s, i) => {
    const m = 'media' in s ? s.media : null;
    if (m?.kind !== 'video' || (s as { voice?: string }).voice !== 'keep') return;
    const rate = m.playbackRate || 1; const a = m.trimStart || 0;
    place(m.url, a, a + s.duration * rate, rate, starts[i], i, 'creator', `s${s.id}`);
  });
  for (const v of voicePartsOf(spec.audio)) {
    let scene = 0; starts.forEach((st, k) => { if (v.at + 0.05 >= st) scene = k; });
    place(v.url, v.from || 0, (v.from || 0) + v.seconds, 1, v.at, scene, 'voice-over', `v${v.id}`);
  }
  return out.sort((x, y) => x.start - y.start);
}

/** The line on screen at `t` (the later one where two overlap). */
export function cueAt(cues: CaptionCue[], t: number): CaptionCue | null {
  let hit: CaptionCue | null = null;
  for (const c of cues) { if (c.start > t) break; if (t < c.end && !c.hidden) hit = c; }
  return hit;
}

/** A video time in the cue's source (for edits made on the timeline). */
export const toSource = (c: Pick<CaptionCue, 'map'>, t: number) => Math.round((c.map.a + (t - c.map.at) * c.map.rate) * 100) / 100;

/** Where a new caption line can go at `t`: the heard source playing there (a kept creator, else the voice-over). */
export function heardAt(spec: VideoSpec, t: number): { url: string; a: number; at: number; rate: number; end: number } | null {
  const starts = sceneStarts(spec).map((f) => f / VIDEO_FPS);
  for (const v of voicePartsOf(spec.audio)) if (t >= v.at && t < v.at + v.seconds) return { url: v.url, a: v.from || 0, at: v.at, rate: 1, end: v.at + v.seconds };
  let i = -1; starts.forEach((st, k) => { if (t >= st) i = k; });
  const s = spec.scenes[i]; const m = s && 'media' in s ? s.media : null;
  if (!s || m?.kind !== 'video' || (s as { voice?: string }).voice !== 'keep') return null;
  return { url: m.url, a: m.trimStart || 0, at: starts[i], rate: m.playbackRate || 1, end: starts[i] + s.duration };
}
